import React, { useState, useEffect, useRef } from 'react';
import {
  FileText, Download, Upload, Eye, Trash2, User, Smartphone, Monitor,
  Calendar, Clock, CheckCircle, XCircle, AlertCircle, AlertTriangle, Search,
  Filter, Plus, X, Building, MapPin, Camera, Mail, Send, Package, Shield, Cpu
} from 'lucide-react';
import { apiService } from '../services/api';
import { showGlobalNotification } from '../hooks/useNotifications';
import { useLanguage } from '../contexts/LanguageContext';

interface AssignmentForm {
  id: number;
  form_number: string;
  form_type: 'assignment' | 'return';
  mobile_phone_id?: number;
  handheld_terminal_id?: number;
  computer_id?: number;
  monitor_id?: number;
  equipment_assignment_id?: number;
  // Equipment fields
  equipment_serial_number?: string;
  equipment_quantity?: number;
  equipment_assignment_type?: string;
  assigned_user_id: number;
  assigned_by_user_id: number;
  assignment_date: string;
  form_status: 'pending' | 'signed' | 'completed';
  signed_form_path?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
  // Joined fields
  inventory_number: string;
  brand: string;
  model: string;
  // Mobile phone fields
  imei1?: string;
  imei2?: string;
  color?: string;
  phone_storage?: string;
  creation_date?: string;
  contract_start_date?: string;
  contract_end_date?: string;
  contract_price?: number;
  monthly_payment?: number;
  // Computer fields
  serial_number?: string;
  processor?: string;
  ram?: string;
  graphics_card?: string;
  computer_storage?: string;
  storage_type?: string;
  purchase_date?: string;
  warranty_end_date?: string;
  // IoT (el terminali) fields
  terminal_serial_number?: string;
  terminal_ip_address?: string;
  terminal_mac_address?: string;
  // Monitor fields
  monitor_serial_number?: string;
  panel_type?: string;
  screen_size?: string;
  // User fields
  assigned_user_name: string;
  assigned_user_email: string;
  assigned_user_phone?: string;
  assigned_user_department?: string;
  assigned_by_name: string;
  assigned_by_email: string;
  // Digital approval fields
  approval_status?: 'pending' | 'approved' | 'rejected' | null;
  digital_approved_at?: string;
}

interface EmailLog {
  id: number;
  assignment_form_id: number;
  sent_to_email: string;
  sent_to_name: string;
  sent_by_name: string;
  status: 'sent' | 'failed';
  error_message?: string;
  sent_at: string;
}

type DeviceTab = 'mobile' | 'computer' | 'monitor' | 'iot' | 'equipment';

const AssignmentFormsPage: React.FC = () => {
  const { t, language } = useLanguage();
  const [forms, setForms] = useState<AssignmentForm[]>([]);
  const [filteredForms, setFilteredForms] = useState<AssignmentForm[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<DeviceTab>('mobile');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [formTypeFilter, setFormTypeFilter] = useState<string>('all'); // 'all', 'assignment', 'return'
  const [currentPageAssignment, setCurrentPageAssignment] = useState(1);
  const [currentPageReturn, setCurrentPageReturn] = useState(1);
  const itemsPerPage = 10;

  // Upload modal states
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedFormForUpload, setSelectedFormForUpload] = useState<AssignmentForm | null>(null);
  const [uploadNotes, setUploadNotes] = useState('');
  const [uploading, setUploading] = useState(false);

  // Camera states
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  // Details modal states
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedFormForDetails, setSelectedFormForDetails] = useState<AssignmentForm | null>(null);
  const [certificateData, setCertificateData] = useState<any>(null);
  const [certificateLoading, setCertificateLoading] = useState(false);

  // Email states
  const [emailLogs, setEmailLogs] = useState<EmailLog[]>([]);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailLogsLoading, setEmailLogsLoading] = useState(false);
  const [sendingApprovalEmail, setSendingApprovalEmail] = useState(false);

  const [onlyMyUnsignedFilter, setOnlyMyUnsignedFilter] = useState(false);

  // Current logged in user object from localStorage
  const currentUser = React.useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('user') || '{}');
    } catch (e) {
      return {};
    }
  }, []);

  const isFormCreatedByCurrentUser = React.useCallback((form: AssignmentForm) => {
    if (!currentUser) return false;
    if (currentUser.id && Number(form.assigned_by_user_id) === Number(currentUser.id)) return true;
    if (currentUser.email && form.assigned_by_email?.toLowerCase() === currentUser.email?.toLowerCase()) return true;
    if (currentUser.name && form.assigned_by_name?.toLowerCase() === currentUser.name?.toLowerCase()) return true;
    if (currentUser.username && form.assigned_by_name?.toLowerCase() === currentUser.username?.toLowerCase()) return true;
    return false;
  }, [currentUser]);

  const isFormUnsigned = (form: AssignmentForm) => {
    return form.form_status !== 'signed' && !form.signed_form_path && form.approval_status !== 'approved';
  };

  const myUnsignedFormsCount = React.useMemo(() => {
    return forms.filter(form => isFormCreatedByCurrentUser(form) && isFormUnsigned(form)).length;
  }, [forms, isFormCreatedByCurrentUser]);

  useEffect(() => {
    loadForms();
  }, []);

  useEffect(() => {
    setCurrentPageAssignment(1);
    setCurrentPageReturn(1);
    filterForms();
  }, [forms, searchTerm, statusFilter, formTypeFilter, activeTab, onlyMyUnsignedFilter]);

  const loadForms = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/assignment-forms', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });
      
      if (!response.ok) {
        throw new Error(t('assignform.errLoadForms'));
      }

      const formsData = await response.json();
      setForms(formsData);
    } catch (error: any) {
      showGlobalNotification.error(t('assignform.notifError'), error.message);
    } finally {
      setLoading(false);
    }
  };

  const filterForms = () => {
    let filtered = forms;

    // Kendi imzasız zimmetlerim filtresi aktifse
    if (onlyMyUnsignedFilter) {
      filtered = filtered.filter(form => isFormCreatedByCurrentUser(form) && isFormUnsigned(form));
    } else {
      // Cihaz tipine göre filtreleme (tab)
      if (activeTab === 'mobile') {
        filtered = filtered.filter(form => form.mobile_phone_id);
      } else if (activeTab === 'computer') {
        filtered = filtered.filter(form => form.computer_id);
      } else if (activeTab === 'monitor') {
        filtered = filtered.filter(form => form.monitor_id);
      } else if (activeTab === 'iot') {
        filtered = filtered.filter(form => form.handheld_terminal_id);
      } else {
        filtered = filtered.filter(form => form.equipment_assignment_id);
      }

      // Durum filtrelemesi
      if (statusFilter !== 'all') {
        filtered = filtered.filter(form => form.form_status === statusFilter);
      }

      // Form türü filtrelemesi
      if (formTypeFilter !== 'all') {
        filtered = filtered.filter(form => form.form_type === formTypeFilter);
      }
    }

    // Arama terimi filtrelemesi
    if (searchTerm) {
      filtered = filtered.filter(form =>
        String(form.form_number || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        String(form.inventory_number || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        String(form.assigned_user_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        String(form.brand || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        String(form.model || '').toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredForms(filtered);
  };

  // Filter forms by type
  const filteredAssignmentForms = filteredForms.filter(f => f.form_type === 'assignment');
  const filteredReturnForms = filteredForms.filter(f => f.form_type === 'return');

  // Pagination helpers for Assignment Forms
  const totalPagesAssignment = Math.ceil(filteredAssignmentForms.length / itemsPerPage);
  const startIndexAssignment = (currentPageAssignment - 1) * itemsPerPage;
  const endIndexAssignment = startIndexAssignment + itemsPerPage;
  const assignmentForms = filteredAssignmentForms.slice(startIndexAssignment, endIndexAssignment);

  // Pagination helpers for Return Forms
  const totalPagesReturn = Math.ceil(filteredReturnForms.length / itemsPerPage);
  const startIndexReturn = (currentPageReturn - 1) * itemsPerPage;
  const endIndexReturn = startIndexReturn + itemsPerPage;
  const returnForms = filteredReturnForms.slice(startIndexReturn, endIndexReturn);

  const totalAssignmentForms = filteredAssignmentForms.length;
  const totalReturnForms = filteredReturnForms.length;

  // Image resize function
  const resizeImage = (file: File, maxWidth: number = 1200, maxHeight: number = 1600, quality: number = 0.8): Promise<File> => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d')!;
      const img = new Image();

      img.onload = () => {
        // Calculate new dimensions
        let { width, height } = img;

        if (width > height) {
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width = (width * maxHeight) / height;
            height = maxHeight;
          }
        }

        canvas.width = width;
        canvas.height = height;

        // Draw and compress
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob((blob) => {
          const resizedFile = new File([blob!], file.name, {
            type: 'image/jpeg',
            lastModified: Date.now()
          });
          resolve(resizedFile);
        }, 'image/jpeg', quality);
      };

      img.src = URL.createObjectURL(file);
    });
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <Clock className="w-4 h-4 text-yellow-600" />;
      case 'signed':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-primary" />;
      default:
        return <AlertCircle className="w-4 h-4 text-gray-600 dark:text-gray-400" />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending':
        return t('assignform.statusPending');
      case 'signed':
        return t('assignform.statusSigned');
      case 'completed':
        return t('assignform.statusCompleted');
      default:
        return t('assignform.statusPending');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'signed':
        return 'bg-green-100 text-green-800';
      case 'completed':
        return 'bg-primary/20 text-primary-dark';
      default:
        return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
    }
  };

  const handleFileSelect = async (file: File) => {
    try {
      setUploading(true);

      // If it's an image, resize it
      let processedFile = file;
      if (file.type.startsWith('image/')) {
        processedFile = await resizeImage(file);

        // Create preview
        const previewUrl = URL.createObjectURL(processedFile);
        setPreviewImage(previewUrl);
      }

      await handleUploadSignedForm(processedFile);
    } catch (error: any) {
      showGlobalNotification.error(t('assignform.notifError'), t('assignform.errFileProcess'));
      setUploading(false);
    }
  };

  const handleUploadSignedForm = async (file: File) => {
    if (!selectedFormForUpload) return;

    try {
      const formData = new FormData();
      formData.append('signedForm', file);
      formData.append('notes', uploadNotes);

      console.log('Uploading file:', {
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
        formId: selectedFormForUpload.id
      });

      // AbortController ile timeout kontrolü
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 120000); // 2 dakika timeout

      const response = await fetch(`/api/assignment-forms/${selectedFormForUpload.id}/upload-signed`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: formData,
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}: ${t('assignform.errFileUpload')}`);
      }

      const result = await response.json();
      console.log('Upload successful:', result);

      showGlobalNotification.success(t('assignform.notifSuccess'), t('assignform.successUpload'));
      setShowUploadModal(false);
      setSelectedFormForUpload(null);
      setUploadNotes('');
      setPreviewImage(null);
      loadForms();
    } catch (error: any) {
      console.error('Upload error:', error);

      let errorMessage = t('assignform.errFileProcess');

      if (error.name === 'AbortError') {
        errorMessage = t('assignform.errUploadTimeout');
      } else if (error.message) {
        errorMessage = error.message;
      }

      showGlobalNotification.error(t('assignform.notifError'), errorMessage);
    } finally {
      setUploading(false);
    }
  };

  const handleCameraCapture = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const downloadSignedForm = async (form: AssignmentForm) => {
    try {
      const response = await fetch(`/api/assignment-forms/${form.id}/download-signed`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}: ${t('assignform.errDownloadFile')}`);
      }

      const blob = await response.blob();

      // Blob boyutunu kontrol et
      if (blob.size === 0) {
        throw new Error(t('assignform.errDownloadEmpty'));
      }

      // Content-Type header'ından uzantı belirle
      const contentType = response.headers.get('content-type') || 'application/octet-stream';
      let ext = '.pdf';
      if (contentType.includes('image/jpeg') || contentType.includes('image/jpg')) {
        ext = '.jpg';
      } else if (contentType.includes('image/png')) {
        ext = '.png';
      } else if (contentType.includes('application/pdf')) {
        ext = '.pdf';
      }

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `zimmet-form-${form.form_number}-signed${ext}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      showGlobalNotification.success(t('assignform.notifSuccess'), t('assignform.successDownload'));
    } catch (error: any) {
      console.error('Download error:', error);
      showGlobalNotification.error(t('assignform.notifError'), error.message || t('assignform.errDownloadGeneral'));
    }
  };

  const deleteForm = async (form: AssignmentForm) => {
    if (!confirm(t('assignform.confirmDelete', { formNo: form.form_number }))) {
      return;
    }

    try {
      const response = await fetch(`/api/assignment-forms/${form.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (!response.ok) {
        throw new Error(t('assignform.errDeleteForm'));
      }

      showGlobalNotification.success(t('assignform.notifSuccess'), t('assignform.successDelete'));
      loadForms();
    } catch (error: any) {
      showGlobalNotification.error(t('assignform.notifError'), error.message);
    }
  };

  const generatePDF = (form: AssignmentForm) => {
    // PDF sayfasını yeni sekmede aç
    window.dispatchEvent(new CustomEvent('navigate', { detail: `assignment-form-pdf/${form.id}` }));
  };

  const loadEmailLogs = async (formId: number) => {
    try {
      setEmailLogsLoading(true);
      const response = await fetch(`/api/assignment-forms/${formId}/email-logs`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      if (response.ok) {
        setEmailLogs(await response.json());
      }
    } catch {
      setEmailLogs([]);
    } finally {
      setEmailLogsLoading(false);
    }
  };

  const loadCertificateData = async (formId: number) => {
    try {
      setCertificateLoading(true);
      const res = await fetch(`/api/approval-tokens/form/${formId}/certificate`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });
      if (res.ok) {
        setCertificateData(await res.json());
      } else {
        setCertificateData(null);
      }
    } catch {
      setCertificateData(null);
    } finally {
      setCertificateLoading(false);
    }
  };

  const sendFormEmail = async (form: AssignmentForm) => {
    if (!form.signed_form_path) {
      showGlobalNotification.error(t('assignform.notifError'), t('assignform.errNoSignedForm'));
      return;
    }
    if (!confirm(t('assignform.confirmSendEmail', { formNo: form.form_number, name: form.assigned_user_name, email: form.assigned_user_email }))) {
      return;
    }
    try {
      setSendingEmail(true);
      const response = await fetch(`/api/assignment-forms/${form.id}/send-email`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || t('assignform.errSendEmail'));
      }
      showGlobalNotification.success(t('assignform.notifSuccess'), data.message);
      if (selectedFormForDetails?.id === form.id) {
        loadEmailLogs(form.id);
      }
    } catch (error: any) {
      showGlobalNotification.error(t('assignform.notifError'), error.message);
    } finally {
      setSendingEmail(false);
    }
  };

  const sendApprovalEmailForForm = async (form: AssignmentForm) => {
    if (!form.assigned_user_email) {
      showGlobalNotification.error(t('assignform.notifError'), 'Kullanıcının e-posta adresi bulunamadı.');
      return;
    }
    if (!confirm(`"${form.form_number}" numaralı zimmet formu için ${form.assigned_user_name} (${form.assigned_user_email}) adresine dijital onay e-postası gönderilsin mi?`)) {
      return;
    }
    try {
      setSendingApprovalEmail(true);
      const response = await fetch(`/api/approval-tokens/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({ assignment_form_id: form.id })
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'E-posta gönderilemedi');
      }
      showGlobalNotification.success(t('assignform.notifSuccess'), data.message);
    } catch (error: any) {
      showGlobalNotification.error(t('assignform.notifError'), error.message);
    } finally {
      setSendingApprovalEmail(false);
    }
  };

  const downloadApprovalCertificate = async (form: AssignmentForm) => {
    try {
      const res = await fetch(`/api/approval-tokens/form/${form.id}/certificate`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });
      if (!res.ok) throw new Error('Dijital onay belgesi alınamadı');
      const cert = await res.json();

      const approvedAt = cert.used_at
        ? new Date(cert.used_at).toLocaleString('tr-TR', { timeZone: 'Europe/Istanbul' })
        : '-';
      const deviceLabel = [form.brand, form.model, form.inventory_number ? `(${form.inventory_number})` : ''].filter(Boolean).join(' ') || '-';
      const assignmentDateStr = form.assignment_date
        ? new Date(form.assignment_date).toLocaleDateString('tr-TR')
        : '-';

      // User-agent kısalt
      const ua = cert.user_agent || '';
      const uaShort = ua.length > 80 ? ua.substring(0, 80) + '...' : ua;

      const html = `<!DOCTYPE html>
<html lang="tr">
<head>
<meta charset="UTF-8" />
<title>Dijital Zimmet Onay Belgesi - ${form.form_number}</title>
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: Arial, sans-serif; color: #1f2937; background: #fff; padding: 32px; }
  .header { text-align: center; border-bottom: 2px solid #4f46e5; padding-bottom: 16px; margin-bottom: 24px; }
  .header h1 { font-size: 20px; color: #4f46e5; margin-bottom: 4px; }
  .header p { font-size: 13px; color: #6b7280; }
  .badge { display: inline-block; background: #d1fae5; color: #065f46; border: 1px solid #6ee7b7; border-radius: 20px; padding: 4px 14px; font-size: 13px; font-weight: bold; margin-bottom: 20px; }
  .section { margin-bottom: 20px; }
  .section-title { font-size: 13px; font-weight: bold; color: #6b7280; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 8px; border-bottom: 1px solid #e5e7eb; padding-bottom: 4px; }
  table { width: 100%; border-collapse: collapse; font-size: 14px; }
  td { padding: 7px 10px; border: 1px solid #e5e7eb; }
  td:first-child { font-weight: 600; background: #f9fafb; width: 38%; color: #374151; }
  .seal { background: #f0fdf4; border: 2px solid #16a34a; border-radius: 8px; padding: 16px; margin-top: 24px; text-align: center; }
  .seal h3 { color: #15803d; font-size: 15px; margin-bottom: 6px; }
  .seal p { font-size: 12px; color: #374151; line-height: 1.5; }
  .token { font-family: monospace; font-size: 12px; background: #f3f4f6; padding: 4px 8px; border-radius: 4px; display: inline-block; margin-top: 4px; }
  .footer { margin-top: 28px; border-top: 1px solid #e5e7eb; padding-top: 12px; text-align: center; font-size: 11px; color: #9ca3af; }
  @media print { body { padding: 16px; } .no-print { display: none; } }
</style>
</head>
<body>
<div class="header">
  <h1>🛡️ Dijital Zimmet Onay Belgesi</h1>
  <p>IT Toolbox · Elektronik İmza Kaydı</p>
</div>

<div style="text-align:center">
  <span class="badge">✓ Dijital Olarak Onaylandı</span>
</div>

<div class="section">
  <div class="section-title">Form Bilgileri</div>
  <table>
    <tr><td>Form Numarası</td><td><strong>${form.form_number}</strong></td></tr>
    <tr><td>Form Tipi</td><td>${form.form_type === 'assignment' ? 'Zimmet Teslim Formu' : 'Zimmet İade Formu'}</td></tr>
    <tr><td>Zimmet Tarihi</td><td>${assignmentDateStr}</td></tr>
  </table>
</div>

<div class="section">
  <div class="section-title">Cihaz Bilgisi</div>
  <table>
    <tr><td>Cihaz</td><td>${deviceLabel}</td></tr>
    ${form.serial_number ? `<tr><td>Seri No</td><td>${form.serial_number}</td></tr>` : ''}
  </table>
</div>

<div class="section">
  <div class="section-title">Zimmetlenen Kişi</div>
  <table>
    <tr><td>Ad Soyad</td><td>${form.assigned_user_name}</td></tr>
    ${form.assigned_user_email ? `<tr><td>E-posta</td><td>${form.assigned_user_email}</td></tr>` : ''}
    ${form.assigned_user_department ? `<tr><td>Departman</td><td>${form.assigned_user_department}</td></tr>` : ''}
    ${form.assigned_user_title ? `<tr><td>Unvan</td><td>${form.assigned_user_title}</td></tr>` : ''}
  </table>
</div>

<div class="section">
  <div class="section-title">Dijital Onay Detayları</div>
  <table>
    <tr><td>Onaylayan</td><td><strong>${cert.approver_name}</strong></td></tr>
    <tr><td>Onay Tarihi / Saati</td><td><strong>${approvedAt}</strong></td></tr>
    <tr><td>IP Adresi</td><td>${cert.ip_address || '-'}</td></tr>
    <tr><td>Tarayıcı / Cihaz</td><td style="font-size:12px">${uaShort || '-'}</td></tr>
    <tr><td>İmza Referans Kodu</td><td><span class="token">${cert.token_masked || '-'}</span></td></tr>
    ${cert.notes ? `<tr><td>Notlar</td><td>${cert.notes}</td></tr>` : ''}
  </table>
</div>

<div class="seal">
  <h3>✅ Elektronik İmza Doğrulaması</h3>
  <p>Bu belge, <strong>${cert.approver_name}</strong> tarafından <strong>${approvedAt}</strong> tarihinde<br/>
  <strong>${cert.ip_address || 'bilinmeyen IP'}</strong> adresinden IT Toolbox sistemi üzerinden dijital olarak imzalanmıştır.<br/>
  İmza kaydı sistem veri tabanında muhafaza edilmekte olup hukuki geçerliliği bulunmaktadır.</p>
</div>

<div class="footer">
  Bu belge IT Toolbox tarafından otomatik oluşturulmuştur · ${new Date().toLocaleString('tr-TR', { timeZone: 'Europe/Istanbul' })}
</div>

<div class="no-print" style="text-align:center; margin-top:24px">
  <button onclick="window.print()" style="background:#4f46e5;color:white;border:none;padding:10px 24px;border-radius:6px;font-size:14px;cursor:pointer">🖨️ Yazdır / PDF Kaydet</button>
</div>
</body>
</html>`;

      const win = window.open('', '_blank');
      if (win) {
        win.document.write(html);
        win.document.close();
        win.focus();
      }
    } catch (error: any) {
      showGlobalNotification.error('Hata', error.message);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-700 dark:bg-gray-900 p-4 sm:p-6">
      <div className="mx-auto">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-3">
                <FileText className="w-6 h-6 sm:w-8 sm:h-8 text-indigo-600" />
                {t('assignform.title')}
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mt-2 text-sm sm:text-base">{t('assignform.subtitle')}</p>
            </div>
          </div>
        </div>

        {/* İmzasız Zimmetlerim Hatırlatıcı Paneli */}
        {myUnsignedFormsCount > 0 && (
          <div className="mb-6 bg-gradient-to-r from-amber-500/15 via-orange-500/15 to-amber-500/15 dark:from-amber-950/50 dark:via-orange-950/50 dark:to-amber-950/50 border-2 border-amber-400/80 dark:border-amber-700/80 rounded-xl p-4 sm:p-5 shadow-sm">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-start gap-3.5">
                <div className="p-2.5 bg-amber-500 text-white rounded-lg shadow-sm flex-shrink-0 animate-pulse">
                  <AlertTriangle className="w-5 h-5 sm:w-6 sm:h-6" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-base sm:text-lg font-bold text-amber-900 dark:text-amber-200">
                      İmzasız Zimmet Evrağı Hatırlatıcısı
                    </h3>
                    <span className="px-2.5 py-0.5 text-xs font-black rounded-full bg-amber-500 text-white">
                      {myUnsignedFormsCount} Adet Eksik
                    </span>
                  </div>
                  <p className="text-xs sm:text-sm text-amber-800 dark:text-amber-300 mt-1">
                    Sayın <strong>{currentUser?.name || currentUser?.username || 'Kullanıcı'}</strong>, teslim ettiğiniz zimmet kayıtlarından <strong>{myUnsignedFormsCount} adet</strong> formun henüz imzalı ıslak veya dijital onay evrağı yüklenmemiştir.
                  </p>
                </div>
              </div>
              
              <button
                type="button"
                onClick={() => {
                  setOnlyMyUnsignedFilter(!onlyMyUnsignedFilter);
                  if (!onlyMyUnsignedFilter) {
                    setStatusFilter('all');
                    setFormTypeFilter('all');
                  }
                }}
                className={`px-4 py-2.5 text-xs sm:text-sm font-bold rounded-lg transition-all shadow-sm flex items-center gap-2 whitespace-nowrap ${
                  onlyMyUnsignedFilter
                    ? 'bg-amber-600 text-white hover:bg-amber-700 ring-2 ring-amber-400'
                    : 'bg-amber-500 text-white hover:bg-amber-600'
                }`}
              >
                <Filter className="w-4 h-4" />
                {onlyMyUnsignedFilter ? 'Tüm Formları Göster' : `İmzasız Zimmetlerimi Filtrele (${myUnsignedFormsCount})`}
              </button>
            </div>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="mb-6">
          <div className="border-b border-gray-200 dark:border-gray-700 overflow-x-auto custom-scrollbar">
            <nav className="-mb-px flex space-x-2 sm:space-x-8 min-w-full" aria-label="Tabs">
              <button
                onClick={() => setActiveTab('mobile')}
                className={`
                  flex-shrink-0 py-3 px-3 sm:px-4 border-b-2 font-medium text-xs sm:text-base transition-colors whitespace-nowrap min-h-[44px]
                  ${activeTab === 'mobile'
                    ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:border-gray-600'
                  }
                `}
              >
                <div className="flex items-center gap-2">
                  <Smartphone className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                  <span>{t('assignform.tabMobile')}</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold flex-shrink-0 ${
                    activeTab === 'mobile' ? 'bg-indigo-100 text-indigo-600 dark:bg-indigo-900/40 dark:text-indigo-300' : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                  }`}>
                    {forms.filter(f => f.mobile_phone_id).length}
                  </span>
                </div>
              </button>
              <button
                onClick={() => setActiveTab('computer')}
                className={`
                  flex-shrink-0 py-3 px-3 sm:px-4 border-b-2 font-medium text-xs sm:text-base transition-colors whitespace-nowrap min-h-[44px]
                  ${activeTab === 'computer'
                    ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:border-gray-600'
                  }
                `}
              >
                <div className="flex items-center gap-2">
                  <Monitor className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                  <span>{t('assignform.tabComputer')}</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold flex-shrink-0 ${
                    activeTab === 'computer' ? 'bg-indigo-100 text-indigo-600 dark:bg-indigo-900/40 dark:text-indigo-300' : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                  }`}>
                    {forms.filter(f => f.computer_id).length}
                  </span>
                </div>
              </button>
              <button
                onClick={() => setActiveTab('monitor')}
                className={`
                  flex-shrink-0 py-3 px-3 sm:px-4 border-b-2 font-medium text-xs sm:text-base transition-colors whitespace-nowrap min-h-[44px]
                  ${activeTab === 'monitor'
                    ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:border-gray-600'
                  }
                `}
              >
                <div className="flex items-center gap-2">
                  <Monitor className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                  <span>{t('assignform.tabMonitor')}</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold flex-shrink-0 ${
                    activeTab === 'monitor' ? 'bg-indigo-100 text-indigo-600 dark:bg-indigo-900/40 dark:text-indigo-300' : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                  }`}>
                    {forms.filter(f => f.monitor_id).length}
                  </span>
                </div>
              </button>
              <button
                onClick={() => setActiveTab('iot')}
                className={`
                  flex-shrink-0 py-3 px-3 sm:px-4 border-b-2 font-medium text-xs sm:text-base transition-colors whitespace-nowrap min-h-[44px]
                  ${activeTab === 'iot'
                    ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:border-gray-600'
                  }
                `}
              >
                <div className="flex items-center gap-2">
                  <Cpu className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                  <span>IoT Ekipman Zimmetleri</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold flex-shrink-0 ${
                    activeTab === 'iot' ? 'bg-indigo-100 text-indigo-600 dark:bg-indigo-900/40 dark:text-indigo-300' : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                  }`}>
                    {forms.filter(f => f.handheld_terminal_id).length}
                  </span>
                </div>
              </button>
              <button
                onClick={() => setActiveTab('equipment')}
                className={`
                  flex-shrink-0 py-3 px-3 sm:px-4 border-b-2 font-medium text-xs sm:text-base transition-colors whitespace-nowrap min-h-[44px]
                  ${activeTab === 'equipment'
                    ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:border-gray-600'
                  }
                `}
              >
                <div className="flex items-center gap-2">
                  <Package className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                  <span>Aksesuar Zimmetleri</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold flex-shrink-0 ${
                    activeTab === 'equipment' ? 'bg-indigo-100 text-indigo-600 dark:bg-indigo-900/40 dark:text-indigo-300' : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                  }`}>
                    {forms.filter(f => f.equipment_assignment_id).length}
                  </span>
                </div>
              </button>
            </nav>
          </div>
        </div>

        {/* Filters */}
        <div className="mb-6 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5" />
                <input
                  type="text"
                  placeholder={t('assignform.searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent min-h-[44px]"
                />
              </div>
            </div>
            
            <div className="flex flex-wrap sm:flex-nowrap items-center gap-3">
              <button
                type="button"
                onClick={() => setOnlyMyUnsignedFilter(!onlyMyUnsignedFilter)}
                className={`px-4 py-3 text-xs sm:text-sm font-bold rounded-lg transition-all border flex items-center gap-2 min-h-[44px] ${
                  onlyMyUnsignedFilter
                    ? 'bg-amber-600 text-white border-amber-700 shadow-sm ring-2 ring-amber-400'
                    : 'bg-amber-50 dark:bg-amber-950/40 text-amber-900 dark:text-amber-200 border-amber-300 dark:border-amber-700 hover:bg-amber-100 dark:hover:bg-amber-900/60'
                }`}
                title="Sadece tarafınızdan teslim edilip imzalı evrağı henüz yüklenmemiş formları gösterir"
              >
                <AlertTriangle className={`w-4 h-4 ${onlyMyUnsignedFilter ? 'text-white' : 'text-amber-600 dark:text-amber-400'}`} />
                <span>Kendi İmzasız Zimmetlerim</span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-black ${onlyMyUnsignedFilter ? 'bg-amber-800 text-white' : 'bg-amber-500 text-white'}`}>
                  {myUnsignedFormsCount}
                </span>
              </button>

              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  if (onlyMyUnsignedFilter) setOnlyMyUnsignedFilter(false);
                }}
                className="px-3 py-3 text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent min-h-[44px]"
              >
                <option value="all">{t('assignform.filterAllStatuses')}</option>
                <option value="pending">{t('assignform.statusPending')}</option>
                <option value="signed">{t('assignform.statusSigned')}</option>
                <option value="completed">{t('assignform.statusCompleted')}</option>
              </select>
            </div>
          </div>

          {/* Active Filter Indicator */}
          {onlyMyUnsignedFilter && (
            <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between text-xs">
              <span className="font-bold text-amber-800 dark:text-amber-300 flex items-center gap-1.5">
                <Filter className="w-3.5 h-3.5 text-amber-600" />
                Filtre Aktif: Kendi teslim ettiğiniz imzalı evrağı eksik olan zimmet formları gösteriliyor ({filteredForms.length} kayıt)
              </span>
              <button
                type="button"
                onClick={() => setOnlyMyUnsignedFilter(false)}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 underline font-semibold"
              >
                Filtreyi Temizle (Tümünü Göster)
              </button>
            </div>
          )}
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 sm:gap-6 mb-8">
          <div
            onClick={() => {
              setOnlyMyUnsignedFilter(false);
              setFormTypeFilter('all');
              setStatusFilter('all');
              setSearchTerm('');
            }}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 sm:p-6 cursor-pointer hover:shadow-md hover:border-gray-300 dark:border-gray-600 dark:hover:border-gray-600 transition-all"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">{t('assignform.cardTotal')}</p>
                <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {activeTab === 'mobile'
                    ? forms.filter(f => f.mobile_phone_id).length
                    : activeTab === 'computer'
                    ? forms.filter(f => f.computer_id).length
                    : activeTab === 'monitor'
                    ? forms.filter(f => f.monitor_id).length
                    : activeTab === 'iot'
                    ? forms.filter(f => f.handheld_terminal_id).length
                    : forms.filter(f => f.equipment_assignment_id).length
                  }
                </p>
              </div>
              {activeTab === 'mobile' ? (
                <Smartphone className="w-6 h-6 sm:w-8 sm:h-8 text-gray-600 dark:text-gray-400" />
              ) : activeTab === 'iot' ? (
                <Cpu className="w-6 h-6 sm:w-8 sm:h-8 text-gray-600 dark:text-gray-400" />
              ) : activeTab === 'equipment' ? (
                <Package className="w-6 h-6 sm:w-8 sm:h-8 text-gray-600 dark:text-gray-400" />
              ) : (
                <Monitor className="w-6 h-6 sm:w-8 sm:h-8 text-gray-600 dark:text-gray-400" />
              )}
            </div>
          </div>

          <div
            onClick={() => {
              setOnlyMyUnsignedFilter(false);
              setFormTypeFilter('assignment');
              setStatusFilter('all');
              setSearchTerm('');
            }}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-indigo-200 dark:border-indigo-900 p-4 sm:p-6 cursor-pointer hover:shadow-md hover:bg-indigo-50 dark:hover:bg-indigo-950 transition-all"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm font-medium text-indigo-600 dark:text-indigo-400 mb-1">{t('assignform.cardAssignment')}</p>
                <p className="text-xl sm:text-2xl font-bold text-indigo-600 dark:text-indigo-400">
                  {activeTab === 'mobile'
                    ? forms.filter(f => f.mobile_phone_id && f.form_type === 'assignment').length
                    : activeTab === 'computer'
                    ? forms.filter(f => f.computer_id && f.form_type === 'assignment').length
                    : activeTab === 'monitor'
                    ? forms.filter(f => f.monitor_id && f.form_type === 'assignment').length
                    : activeTab === 'iot'
                    ? forms.filter(f => f.handheld_terminal_id && f.form_type === 'assignment').length
                    : forms.filter(f => f.equipment_assignment_id && f.form_type === 'assignment').length
                  }
                </p>
              </div>
              <FileText className="w-6 h-6 sm:w-8 sm:h-8 text-indigo-600 dark:text-indigo-400" />
            </div>
          </div>

          <div
            onClick={() => {
              setOnlyMyUnsignedFilter(false);
              setFormTypeFilter('return');
              setStatusFilter('all');
              setSearchTerm('');
            }}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-purple-200 dark:border-purple-900 p-4 sm:p-6 cursor-pointer hover:shadow-md hover:bg-purple-50 dark:hover:bg-purple-950 transition-all"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm font-medium text-purple-600 dark:text-purple-400 mb-1">{t('assignform.cardReturn')}</p>
                <p className="text-xl sm:text-2xl font-bold text-purple-600 dark:text-purple-400">
                  {activeTab === 'mobile'
                    ? forms.filter(f => f.mobile_phone_id && f.form_type === 'return').length
                    : activeTab === 'computer'
                    ? forms.filter(f => f.computer_id && f.form_type === 'return').length
                    : activeTab === 'monitor'
                    ? forms.filter(f => f.monitor_id && f.form_type === 'return').length
                    : activeTab === 'iot'
                    ? forms.filter(f => f.handheld_terminal_id && f.form_type === 'return').length
                    : forms.filter(f => f.equipment_assignment_id && f.form_type === 'return').length
                  }
                </p>
              </div>
              <FileText className="w-6 h-6 sm:w-8 sm:h-8 text-purple-600 dark:text-purple-400" />
            </div>
          </div>

          <div
            onClick={() => {
              setOnlyMyUnsignedFilter(false);
              setFormTypeFilter('all');
              setStatusFilter('pending');
              setSearchTerm('');
            }}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-orange-200 dark:border-orange-900 p-4 sm:p-6 cursor-pointer hover:shadow-md hover:bg-orange-50 dark:hover:bg-orange-950 transition-all"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm font-medium text-orange-600 dark:text-orange-400 mb-1">{t('assignform.cardUnsigned')}</p>
                <p className="text-xl sm:text-2xl font-bold text-orange-600 dark:text-orange-400">
                  {activeTab === 'mobile'
                    ? forms.filter(f => f.mobile_phone_id && f.form_status === 'pending' && f.approval_status !== 'approved').length
                    : activeTab === 'computer'
                    ? forms.filter(f => f.computer_id && f.form_status === 'pending' && f.approval_status !== 'approved').length
                    : activeTab === 'monitor'
                    ? forms.filter(f => f.monitor_id && f.form_status === 'pending' && f.approval_status !== 'approved').length
                    : activeTab === 'iot'
                    ? forms.filter(f => f.handheld_terminal_id && f.form_status === 'pending' && f.approval_status !== 'approved').length
                    : forms.filter(f => f.equipment_assignment_id && f.form_status === 'pending' && f.approval_status !== 'approved').length
                  }
                </p>
              </div>
              <Clock className="w-6 h-6 sm:w-8 sm:h-8 text-orange-600 dark:text-orange-400" />
            </div>
          </div>

          {/* Kendi İmzasız Zimmetlerim Özel Kartı */}
          <div
            onClick={() => {
              setOnlyMyUnsignedFilter(!onlyMyUnsignedFilter);
              setSearchTerm('');
            }}
            className={`rounded-lg shadow-sm border p-4 sm:p-6 cursor-pointer hover:shadow-md transition-all ${
              onlyMyUnsignedFilter
                ? 'bg-amber-500 border-amber-600 text-white ring-2 ring-amber-400 dark:bg-amber-600'
                : 'bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/40 dark:to-orange-950/40 border-amber-300 dark:border-amber-700 hover:bg-amber-100/80 dark:hover:bg-amber-900/60'
            }`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-xs sm:text-sm font-bold mb-1 ${onlyMyUnsignedFilter ? 'text-white' : 'text-amber-900 dark:text-amber-200'}`}>
                  Kendi İmzasızlarım
                </p>
                <p className={`text-xl sm:text-2xl font-black ${onlyMyUnsignedFilter ? 'text-white' : 'text-amber-600 dark:text-amber-400'}`}>
                  {myUnsignedFormsCount}
                </p>
              </div>
              <AlertTriangle className={`w-6 h-6 sm:w-8 sm:h-8 ${onlyMyUnsignedFilter ? 'text-white' : 'text-amber-600 dark:text-amber-400 animate-pulse'}`} />
            </div>
          </div>
        </div>

        {/* Zimmet Teslim Formları Tablosu */}
        {assignmentForms.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 mb-8">
          <div className="px-4 sm:px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-indigo-50 dark:bg-indigo-950">
            <h3 className="text-base sm:text-lg font-semibold text-indigo-900 dark:text-indigo-100">
              {t('assignform.sectionAssignment', { count: totalAssignmentForms })}
            </h3>
          </div>
          <div className="overflow-x-auto w-full">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('assignform.colFormInfo')}
                  </th>
                  <th className="hidden md:table-cell px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {activeTab === 'mobile' ? t('assignform.colMobile') : activeTab === 'computer' ? t('assignform.colComputer') : activeTab === 'monitor' ? t('assignform.colMonitor') : activeTab === 'iot' ? 'IoT Ekipmanı' : 'Aksesuar'}
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('assignform.colUser')}
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('assignform.colStatus')}
                  </th>
                  <th className="hidden sm:table-cell px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('assignform.colDate')}
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('assignform.colActions')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {assignmentForms.map((form) => (
                  <tr key={form.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {form.form_number}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {t('assignform.createdBy', { name: form.assigned_by_name })}
                      </div>
                    </td>
                    <td className="hidden md:table-cell px-4 sm:px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {activeTab === 'mobile' ? (
                          <Smartphone className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                        ) : activeTab === 'iot' ? (
                          <Cpu className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                        ) : activeTab === 'equipment' ? (
                          <Package className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                        ) : (
                          <Monitor className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                        )}
                        <div>
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {form.brand} {form.model}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {form.inventory_number}
                            {activeTab === 'equipment' && form.equipment_quantity && form.equipment_quantity > 1 && (
                              <span className="ml-1">({form.equipment_quantity} adet)</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <User className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                        <div>
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {form.assigned_user_name}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400 hidden sm:block">
                            {form.assigned_user_email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                      <div className="space-y-1">
                        {form.approval_status === 'approved' ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Dijital Onaylı
                          </span>
                        ) : (
                          <>
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(form.form_status)}`}>
                              {getStatusIcon(form.form_status)}
                              <span className="ml-1">{getStatusText(form.form_status)}</span>
                            </span>
                            {form.approval_status === 'rejected' && (
                              <div>
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400">
                                  <XCircle className="w-3 h-3 mr-1" />
                                  Reddedildi
                                </span>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                    <td className="hidden sm:table-cell px-4 sm:px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Calendar className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                        <div className="text-sm text-gray-900 dark:text-gray-100">
                          {new Date(form.assignment_date).toLocaleDateString(language === 'en' ? 'en-US' : 'tr-TR')}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => {
                            setSelectedFormForDetails(form);
                            setEmailLogs([]);
                            setCertificateData(null);
                            setShowDetailsModal(true);
                            loadEmailLogs(form.id);
                            if (form.approval_status === 'approved') loadCertificateData(form.id);
                          }}
                          className="text-indigo-600 hover:text-indigo-900"
                          title={t('assignform.actionDetails')}
                        >
                          <Eye className="w-4 h-4" />
                        </button>

                        <button
                          onClick={() => generatePDF(form)}
                          className="text-primary hover:text-primary-dark"
                          title={t('assignform.actionPDF')}
                        >
                          <FileText className="w-4 h-4" />
                        </button>

                        {form.signed_form_path && (
                          <button
                            onClick={() => downloadSignedForm(form)}
                            className="text-green-600 hover:text-green-900"
                            title={t('assignform.actionDownloadSigned')}
                          >
                            <Download className="w-4 h-4" />
                          </button>
                        )}

                        {form.signed_form_path && (
                          <button
                            onClick={() => sendFormEmail(form)}
                            disabled={sendingEmail}
                            className="text-cyan-600 hover:text-cyan-900 disabled:opacity-50"
                            title={t('assignform.actionSendEmail', { email: form.assigned_user_email })}
                          >
                            <Mail className="w-4 h-4" />
                          </button>
                        )}

                        {form.approval_status === 'approved' ? (
                          <button
                            onClick={() => downloadApprovalCertificate(form)}
                            className="text-green-600 hover:text-green-900"
                            title="Dijital onay belgesini görüntüle / indir"
                          >
                            <Shield className="w-4 h-4" />
                          </button>
                        ) : (
                          <button
                            onClick={() => sendApprovalEmailForForm(form)}
                            disabled={sendingApprovalEmail}
                            className="text-indigo-500 hover:text-indigo-800 disabled:opacity-50"
                            title={`Dijital onay e-postası gönder (${form.assigned_user_email})`}
                          >
                            <Send className="w-4 h-4" />
                          </button>
                        )}

                        {form.approval_status !== 'approved' && (
                          <button
                            onClick={() => {
                              setSelectedFormForUpload(form);
                              setUploadNotes('');
                              setPreviewImage(null);
                              setShowUploadModal(true);
                            }}
                            className={form.signed_form_path ? "text-purple-600 hover:text-purple-900" : "text-orange-600 hover:text-orange-900"}
                            title={form.signed_form_path ? t('assignform.actionReuploadSigned') : t('assignform.actionUploadSigned')}
                          >
                            <Upload className="w-4 h-4" />
                          </button>
                        )}

                        <button
                          onClick={() => deleteForm(form)}
                          className="text-red-600 hover:text-red-900"
                          title={t('assignform.actionDelete')}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {assignmentForms.length === 0 && (
            <div className="text-center py-12">
              <FileText className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">{t('assignform.emptyAssignment')}</h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {searchTerm || statusFilter !== 'all'
                  ? t('assignform.emptySearch')
                  : t('assignform.emptyNoAssignment')
                }
              </p>
            </div>
          )}

          {/* Pagination Controls for Assignment Forms */}
          {filteredAssignmentForms.length > 0 && totalPagesAssignment > 1 && (
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-indigo-50">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {t('assignform.paginationInfo', { total: filteredAssignmentForms.length, page: currentPageAssignment, totalPages: totalPagesAssignment })}
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setCurrentPageAssignment(prev => Math.max(1, prev - 1))}
                    disabled={currentPageAssignment === 1}
                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t('assignform.paginationPrev')}
                  </button>

                  {Array.from({ length: totalPagesAssignment }, (_, i) => i + 1).map(page => (
                    <button
                      key={page}
                      onClick={() => setCurrentPageAssignment(page)}
                      className={`px-3 py-2 rounded-md text-sm font-medium ${
                        currentPageAssignment === page
                          ? 'bg-indigo-600 text-white'
                          : 'border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700'
                      }`}
                    >
                      {page}
                    </button>
                  ))}

                  <button
                    onClick={() => setCurrentPageAssignment(prev => Math.min(totalPagesAssignment, prev + 1))}
                    disabled={currentPageAssignment === totalPagesAssignment}
                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t('assignform.paginationNext')}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
        )}

        {/* Zimmet Geri Teslim Formları Tablosu */}
        {returnForms.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="px-4 sm:px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-purple-50 dark:bg-purple-950">
            <h3 className="text-base sm:text-lg font-semibold text-purple-900 dark:text-purple-100">
              {t('assignform.sectionReturn', { count: totalReturnForms })}
            </h3>
          </div>
          <div className="overflow-x-auto w-full">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('assignform.colFormInfo')}
                  </th>
                  <th className="hidden md:table-cell px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {activeTab === 'mobile' ? t('assignform.colMobile') : activeTab === 'computer' ? t('assignform.colComputer') : activeTab === 'monitor' ? t('assignform.colMonitor') : activeTab === 'iot' ? 'IoT Ekipmanı' : 'Aksesuar'}
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('assignform.colUser')}
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('assignform.colStatus')}
                  </th>
                  <th className="hidden sm:table-cell px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('assignform.colDate')}
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('assignform.colActions')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {returnForms.map((form) => (
                  <tr key={form.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {form.form_number}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {t('assignform.createdBy', { name: form.assigned_by_name })}
                      </div>
                    </td>
                    <td className="hidden md:table-cell px-4 sm:px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {activeTab === 'mobile' ? (
                          <Smartphone className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                        ) : activeTab === 'iot' ? (
                          <Cpu className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                        ) : activeTab === 'equipment' ? (
                          <Package className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                        ) : (
                          <Monitor className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                        )}
                        <div>
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {form.brand} {form.model}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {form.inventory_number}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <User className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                        <div>
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {form.assigned_user_name}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400 hidden sm:block">
                            {form.assigned_user_email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(form.form_status)}`}>
                        {getStatusIcon(form.form_status)}
                        <span className="ml-1">{getStatusText(form.form_status)}</span>
                      </span>
                    </td>
                    <td className="hidden sm:table-cell px-4 sm:px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Calendar className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                        <div className="text-sm text-gray-900 dark:text-gray-100">
                          {new Date(form.assignment_date).toLocaleDateString(language === 'en' ? 'en-US' : 'tr-TR')}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => {
                            setSelectedFormForDetails(form);
                            setEmailLogs([]);
                            setCertificateData(null);
                            setShowDetailsModal(true);
                            loadEmailLogs(form.id);
                            if (form.approval_status === 'approved') loadCertificateData(form.id);
                          }}
                          className="text-indigo-600 hover:text-indigo-900"
                          title={t('assignform.actionDetails')}
                        >
                          <Eye className="w-4 h-4" />
                        </button>

                        <button
                          onClick={() => generatePDF(form)}
                          className="text-primary hover:text-primary-dark"
                          title={t('assignform.actionPDF')}
                        >
                          <FileText className="w-4 h-4" />
                        </button>

                        {form.signed_form_path && (
                          <button
                            onClick={() => downloadSignedForm(form)}
                            className="text-green-600 hover:text-green-900"
                            title={t('assignform.actionDownloadSigned')}
                          >
                            <Download className="w-4 h-4" />
                          </button>
                        )}

                        {form.signed_form_path && (
                          <button
                            onClick={() => sendFormEmail(form)}
                            disabled={sendingEmail}
                            className="text-cyan-600 hover:text-cyan-900 disabled:opacity-50"
                            title={t('assignform.actionSendEmail', { email: form.assigned_user_email })}
                          >
                            <Mail className="w-4 h-4" />
                          </button>
                        )}

                        <button
                          onClick={() => {
                            setSelectedFormForUpload(form);
                            setUploadNotes('');
                            setPreviewImage(null);
                            setShowUploadModal(true);
                          }}
                          className={form.signed_form_path ? "text-purple-600 hover:text-purple-900" : "text-orange-600 hover:text-orange-900"}
                          title={form.signed_form_path ? t('assignform.actionReuploadSigned') : t('assignform.actionUploadSigned')}
                        >
                          <Upload className="w-4 h-4" />
                        </button>

                        <button
                          onClick={() => deleteForm(form)}
                          className="text-red-600 hover:text-red-900"
                          title={t('assignform.actionDelete')}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {returnForms.length === 0 && (
            <div className="text-center py-12">
              <FileText className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">{t('assignform.emptyReturn')}</h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {searchTerm || statusFilter !== 'all'
                  ? t('assignform.emptySearch')
                  : t('assignform.emptyNoReturn')
                }
              </p>
            </div>
          )}

          {/* Pagination Controls for Return Forms */}
          {filteredReturnForms.length > 0 && totalPagesReturn > 1 && (
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-purple-50">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {t('assignform.paginationInfo', { total: filteredReturnForms.length, page: currentPageReturn, totalPages: totalPagesReturn })}
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setCurrentPageReturn(prev => Math.max(1, prev - 1))}
                    disabled={currentPageReturn === 1}
                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t('assignform.paginationPrev')}
                  </button>

                  {Array.from({ length: totalPagesReturn }, (_, i) => i + 1).map(page => (
                    <button
                      key={page}
                      onClick={() => setCurrentPageReturn(page)}
                      className={`px-3 py-2 rounded-md text-sm font-medium ${
                        currentPageReturn === page
                          ? 'bg-purple-600 text-white'
                          : 'border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700'
                      }`}
                    >
                      {page}
                    </button>
                  ))}

                  <button
                    onClick={() => setCurrentPageReturn(prev => Math.min(totalPagesReturn, prev + 1))}
                    disabled={currentPageReturn === totalPagesReturn}
                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t('assignform.paginationNext')}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
        )}

        {filteredForms.length === 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 mt-8 p-12 text-center">
            <FileText className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-1">{t('assignform.emptyAll')}</h3>
            <p className="text-gray-500 dark:text-gray-400">
              {searchTerm || statusFilter !== 'all' || formTypeFilter !== 'all'
                ? t('assignform.emptyAllSearch')
                : t('assignform.emptyAllNone')
              }
            </p>
          </div>
        )}

        {/* Upload Modal */}
        {showUploadModal && selectedFormForUpload && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full">
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {selectedFormForUpload.signed_form_path ? t('assignform.reuploadTitle') : t('assignform.uploadTitle')}
                  </h3>
                  <button
                    onClick={() => {
                      setShowUploadModal(false);
                      setPreviewImage(null);
                    }}
                    className="text-gray-400 hover:text-gray-600 dark:text-gray-400"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <div className="p-6">
                {selectedFormForUpload.signed_form_path && (
                  <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                    <p className="text-sm text-yellow-800">
                      {t('assignform.uploadWarning')}
                    </p>
                  </div>
                )}

                <div className="mb-4">
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                    {t('assignform.uploadFormNo')} <strong>{selectedFormForUpload.form_number}</strong>
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    {t('assignform.uploadDevice')} <strong>{selectedFormForUpload.brand} {selectedFormForUpload.model}</strong>
                  </p>
                </div>

                {/* Preview Image */}
                {previewImage && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('assignform.uploadPreview')}
                    </label>
                    <div className="relative">
                      <img
                        src={previewImage}
                        alt="Preview"
                        className="w-full max-h-48 object-contain border border-gray-300 dark:border-gray-600 rounded-md"
                      />
                      <button
                        onClick={() => setPreviewImage(null)}
                        className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600"
                      >
                        ×
                      </button>
                    </div>
                  </div>
                )}

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('assignform.uploadFileLabel')}
                  </label>

                  {/* Upload Options */}
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    <button
                      onClick={handleCameraCapture}
                      className="flex items-center justify-center px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-primary/10 hover:bg-primary/20 transition-colors"
                      disabled={uploading}
                    >
                      <Camera className="w-4 h-4 mr-2" />
                      {t('assignform.uploadCamera')}
                    </button>
                    <button
                      onClick={() => {
                        if (fileInputRef.current) {
                          fileInputRef.current.removeAttribute('capture');
                          fileInputRef.current.click();
                        }
                      }}
                      className="flex items-center justify-center px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      disabled={uploading}
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      {t('assignform.uploadChooseFile')}
                    </button>
                  </div>

                  {/* Hidden file input */}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    capture="environment"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        handleFileSelect(file);
                      }
                      e.target.value = '';
                    }}
                    className="hidden"
                  />
                </div>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('assignform.uploadNotesLabel')}
                  </label>
                  <textarea
                    value={uploadNotes}
                    onChange={(e) => setUploadNotes(e.target.value)}
                    placeholder={t('assignform.uploadNotesPlaceholder')}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    rows={3}
                    disabled={uploading}
                  />
                </div>
                
                {uploading && (
                  <div className="text-center py-4">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">{t('assignform.uploading')}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Details Modal */}
        {showDetailsModal && selectedFormForDetails && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {t('assignform.detailsTitle')}
                  </h3>
                  <button
                    onClick={() => setShowDetailsModal(false)}
                    className="text-gray-400 hover:text-gray-600 dark:text-gray-400"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
              
              <div className="p-6 space-y-6">
                {/* Form Bilgileri */}
                <div>
                  <h4 className="text-md font-semibold text-gray-900 dark:text-gray-100 mb-3">{t('assignform.detailsFormInfo')}</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('assignform.detailsFormNumber')}</label>
                      <p className="text-sm text-gray-900 dark:text-gray-100">{selectedFormForDetails.form_number}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('assignform.detailsStatus')}</label>
                      {selectedFormForDetails.approval_status === 'approved' ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                          <Shield className="w-3.5 h-3.5 mr-1" />
                          Dijital Onaylı
                        </span>
                      ) : (
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(selectedFormForDetails.form_status)}`}>
                          {getStatusIcon(selectedFormForDetails.form_status)}
                          <span className="ml-1">{getStatusText(selectedFormForDetails.form_status)}</span>
                        </span>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('assignform.detailsAssignmentDate')}</label>
                      <p className="text-sm text-gray-900 dark:text-gray-100">
                        {new Date(selectedFormForDetails.assignment_date).toLocaleDateString(language === 'en' ? 'en-US' : 'tr-TR')}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('assignform.detailsCreatedAt')}</label>
                      <p className="text-sm text-gray-900 dark:text-gray-100">
                        {new Date(selectedFormForDetails.created_at).toLocaleDateString(language === 'en' ? 'en-US' : 'tr-TR')}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Cihaz Bilgileri */}
                <div>
                  <h4 className="text-md font-semibold text-gray-900 dark:text-gray-100 mb-3">
                    {selectedFormForDetails.mobile_phone_id ? t('assignform.detailsDeviceInfoMobile') : selectedFormForDetails.handheld_terminal_id ? 'IoT Ekipman Bilgileri' : t('assignform.detailsDeviceInfoComputer')}
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('assignform.detailsInventoryNo')}</label>
                      <p className="text-sm text-gray-900 dark:text-gray-100">{selectedFormForDetails.inventory_number}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('assignform.detailsBrandModel')}</label>
                      <p className="text-sm text-gray-900 dark:text-gray-100">{selectedFormForDetails.brand} {selectedFormForDetails.model}</p>
                    </div>
                    {/* Mobile Phone Specific Fields */}
                    {selectedFormForDetails.mobile_phone_id && selectedFormForDetails.imei1 && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">IMEI 1</label>
                        <p className="text-sm text-gray-900 dark:text-gray-100">{selectedFormForDetails.imei1}</p>
                      </div>
                    )}
                    {selectedFormForDetails.mobile_phone_id && selectedFormForDetails.imei2 && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">IMEI 2</label>
                        <p className="text-sm text-gray-900 dark:text-gray-100">{selectedFormForDetails.imei2}</p>
                      </div>
                    )}
                    {selectedFormForDetails.mobile_phone_id && selectedFormForDetails.color && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('assignform.detailsColor')}</label>
                        <p className="text-sm text-gray-900 dark:text-gray-100">{selectedFormForDetails.color}</p>
                      </div>
                    )}
                    {selectedFormForDetails.mobile_phone_id && selectedFormForDetails.phone_storage && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('assignform.detailsStorage')}</label>
                        <p className="text-sm text-gray-900 dark:text-gray-100">{selectedFormForDetails.phone_storage}</p>
                      </div>
                    )}
                    {/* IoT (El Terminali) Specific Fields */}
                    {selectedFormForDetails.handheld_terminal_id && selectedFormForDetails.terminal_serial_number && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('assignform.detailsSerialNo')}</label>
                        <p className="text-sm text-gray-900 dark:text-gray-100">{selectedFormForDetails.terminal_serial_number}</p>
                      </div>
                    )}
                    {selectedFormForDetails.handheld_terminal_id && selectedFormForDetails.terminal_ip_address && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">IP</label>
                        <p className="text-sm text-gray-900 dark:text-gray-100">{selectedFormForDetails.terminal_ip_address}</p>
                      </div>
                    )}
                    {selectedFormForDetails.handheld_terminal_id && selectedFormForDetails.terminal_mac_address && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">MAC</label>
                        <p className="text-sm text-gray-900 dark:text-gray-100">{selectedFormForDetails.terminal_mac_address}</p>
                      </div>
                    )}
                    {/* Computer Specific Fields */}
                    {selectedFormForDetails.computer_id && selectedFormForDetails.serial_number && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('assignform.detailsSerialNo')}</label>
                        <p className="text-sm text-gray-900 dark:text-gray-100">{selectedFormForDetails.serial_number}</p>
                      </div>
                    )}
                    {selectedFormForDetails.computer_id && selectedFormForDetails.processor && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('assignform.detailsProcessor')}</label>
                        <p className="text-sm text-gray-900 dark:text-gray-100">{selectedFormForDetails.processor}</p>
                      </div>
                    )}
                    {selectedFormForDetails.computer_id && selectedFormForDetails.ram && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">RAM</label>
                        <p className="text-sm text-gray-900 dark:text-gray-100">{selectedFormForDetails.ram}</p>
                      </div>
                    )}
                    {selectedFormForDetails.computer_id && selectedFormForDetails.graphics_card && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('assignform.detailsGraphicsCard')}</label>
                        <p className="text-sm text-gray-900 dark:text-gray-100">{selectedFormForDetails.graphics_card}</p>
                      </div>
                    )}
                    {selectedFormForDetails.computer_id && selectedFormForDetails.computer_storage && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('assignform.detailsStorage')}</label>
                        <p className="text-sm text-gray-900 dark:text-gray-100">
                          {selectedFormForDetails.computer_storage}
                          {selectedFormForDetails.storage_type && ` (${selectedFormForDetails.storage_type})`}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Kullanıcı Bilgileri */}
                <div>
                  <h4 className="text-md font-semibold text-gray-900 dark:text-gray-100 mb-3">{t('assignform.detailsAssignedUser')}</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('assignform.detailsFullName')}</label>
                      <p className="text-sm text-gray-900 dark:text-gray-100">{selectedFormForDetails.assigned_user_name}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('assignform.detailsEmail')}</label>
                      <p className="text-sm text-gray-900 dark:text-gray-100">{selectedFormForDetails.assigned_user_email}</p>
                    </div>
                    {selectedFormForDetails.assigned_user_phone && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('assignform.detailsPhone')}</label>
                        <p className="text-sm text-gray-900 dark:text-gray-100">{selectedFormForDetails.assigned_user_phone}</p>
                      </div>
                    )}
                    {selectedFormForDetails.assigned_user_department && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('assignform.detailsDepartment')}</label>
                        <p className="text-sm text-gray-900 dark:text-gray-100">{selectedFormForDetails.assigned_user_department}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Oluşturan Kişi */}
                <div>
                  <h4 className="text-md font-semibold text-gray-900 dark:text-gray-100 mb-3">{t('assignform.detailsCreatedBy')}</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('assignform.detailsFullName')}</label>
                      <p className="text-sm text-gray-900 dark:text-gray-100">{selectedFormForDetails.assigned_by_name}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('assignform.detailsEmail')}</label>
                      <p className="text-sm text-gray-900 dark:text-gray-100">{selectedFormForDetails.assigned_by_email}</p>
                    </div>
                  </div>
                </div>

                {/* Notlar */}
                {selectedFormForDetails.notes && (
                  <div>
                    <h4 className="text-md font-semibold text-gray-900 dark:text-gray-100 mb-3">{t('assignform.detailsNotes')}</h4>
                    <p className="text-sm text-gray-900 dark:text-gray-100 bg-gray-50 dark:bg-gray-700 p-3 rounded-md">
                      {selectedFormForDetails.notes}
                    </p>
                  </div>
                )}

                {/* Dijital Onay Bilgileri */}
                {selectedFormForDetails.approval_status === 'approved' && (
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-md font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                        <Shield className="w-4 h-4 text-emerald-600" />
                        Dijital Onay Bilgileri
                      </h4>
                      <button
                        onClick={() => downloadApprovalCertificate(selectedFormForDetails)}
                        className="flex items-center gap-1 px-3 py-1.5 text-sm bg-emerald-600 text-white rounded-md hover:bg-emerald-700 transition-colors"
                      >
                        <Shield className="w-3.5 h-3.5" />
                        Belgeyi Görüntüle
                      </button>
                    </div>
                    {certificateLoading ? (
                      <div className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">Yükleniyor...</div>
                    ) : certificateData ? (
                      <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-700 rounded-md p-4 space-y-2">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">Onaylayan</label>
                            <p className="text-sm text-gray-900 dark:text-gray-100 font-medium">{certificateData.approver_name}</p>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">E-posta</label>
                            <p className="text-sm text-gray-900 dark:text-gray-100">{certificateData.approver_email}</p>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">Onay Tarihi</label>
                            <p className="text-sm text-gray-900 dark:text-gray-100">
                              {certificateData.used_at ? new Date(certificateData.used_at).toLocaleString(language === 'en' ? 'en-US' : 'tr-TR') : '-'}
                            </p>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">IP Adresi</label>
                            <p className="text-sm text-gray-900 dark:text-gray-100 font-mono">{certificateData.ip_address || '-'}</p>
                          </div>
                          {certificateData.notes && (
                            <div className="col-span-2">
                              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">Not</label>
                              <p className="text-sm text-gray-900 dark:text-gray-100">{certificateData.notes}</p>
                            </div>
                          )}
                          {certificateData.token_masked && (
                            <div className="col-span-2">
                              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">Referans No</label>
                              <p className="text-xs text-gray-600 dark:text-gray-400 font-mono">{certificateData.token_masked}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="text-sm text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 p-3 rounded-md text-center">
                        Onay detayları bulunamadı
                      </div>
                    )}
                  </div>
                )}

                {/* E-posta Gönderim Geçmişi */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-md font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                      <Mail className="w-4 h-4" />
                      {t('assignform.emailHistory')}
                    </h4>
                    {selectedFormForDetails.signed_form_path && selectedFormForDetails.approval_status !== 'approved' && (
                      <button
                        onClick={() => sendFormEmail(selectedFormForDetails)}
                        disabled={sendingEmail}
                        className="flex items-center gap-1 px-3 py-1.5 text-sm bg-cyan-600 text-white rounded-md hover:bg-cyan-700 transition-colors disabled:opacity-50"
                      >
                        <Send className="w-3.5 h-3.5" />
                        {sendingEmail ? t('assignform.emailSending') : t('assignform.emailSend')}
                      </button>
                    )}
                  </div>
                  {emailLogsLoading ? (
                    <div className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">{t('assignform.emailHistoryLoading')}</div>
                  ) : emailLogs.length === 0 ? (
                    <div className="text-sm text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 p-3 rounded-md text-center">
                      {t('assignform.emailHistoryEmpty')}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {emailLogs.map(log => (
                        <div key={log.id} className={`flex items-start justify-between p-3 rounded-md border ${log.status === 'sent' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                          <div className="flex items-start gap-2">
                            {log.status === 'sent' ? (
                              <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                            ) : (
                              <XCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                            )}
                            <div>
                              <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                {log.status === 'sent' ? t('assignform.emailSent') : t('assignform.emailFailed')} → {log.sent_to_email}
                              </div>
                              {log.sent_by_name && (
                                <div className="text-xs text-gray-500 dark:text-gray-400">{t('assignform.emailSentBy', { name: log.sent_by_name })}</div>
                              )}
                              {log.error_message && (
                                <div className="text-xs text-red-600 mt-1">{log.error_message}</div>
                              )}
                            </div>
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0 ml-2">
                            {new Date(log.sent_at).toLocaleString(language === 'en' ? 'en-US' : 'tr-TR')}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end">
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
                >
                  {t('assignform.close')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AssignmentFormsPage;
